import React, { useMemo, useState } from "react";
import { ArrowLeft, Hash, Lock, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Composer } from "@/components/meldinger/Composer";
import { RecipientPicker, SharePicker } from "@/components/meldinger/Pickers";
import { workItems } from "@/lib/krav";

/**
 * Ny samtale, chat-first: the case is the room, the first message starts the thread. No form – a composer, optionally
 * tied to a post, and for a private conversation the people who are in it. The title is made from the post or the first
 * sentence and can be changed later.
 */
export function NewThread({ ctx, ops, onDone, onCancel, preset, mobile, mode = "intern" }) {
  const privat = mode === "privat";
  const [to, setTo] = useState([]);
  const [itemIds, setItemIds] = useState(() => preset?.item_ids || []);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const items = useMemo(() => workItems(ctx?.items || []), [ctx]);
  const chosen = itemIds.map((id) => (ctx?.items || []).flatMap((i) => [i, ...(i.children || [])]).find((i) => i.id === id)).filter(Boolean);
  const send = async (body) => {
    setBusy(true);
    try {
      const r = await ops.thread.create({ ...body, to: to.map((p) => p.id), item_ids: itemIds, doc_id: preset?.doc_id || null, private: privat });
      onDone(r.thread.id);
    } finally { setBusy(false); }
  };
  const link = items.length ? (
    <Popover open={picking} onOpenChange={setPicking}>
      <PopoverTrigger asChild><button type="button" className="inline-flex h-8 items-center gap-1 rounded-full px-2 text-[12.5px] text-foreground/60 transition-colors hover:bg-black/[0.05] hover:text-foreground" title="Knytt samtalen til en arbeidspost" data-testid="thread-new-link-item"><Hash className="h-3.5 w-3.5" /> {chosen.length ? "Flere poster" : "Knytt til post"}</button></PopoverTrigger>
      <PopoverContent align="start" side="top" sideOffset={8} className="w-auto border-0 bg-transparent p-0 shadow-none" aria-describedby={undefined}>
        <SharePicker ctx={ctx} kind="item" onPick={(r) => { setItemIds((v) => (v.includes(r.id) ? v : [...v, r.id])); setPicking(false); }} onClose={() => setPicking(false)} testId="thread-new-item-picker" />
      </PopoverContent>
    </Popover>
  ) : null;
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="thread-new" data-mode={mode}>
      <header className="flex shrink-0 items-center gap-2 px-4 py-2.5 sm:px-6">
        {mobile ? <button type="button" onClick={onCancel} className="-ml-1.5 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-black/[0.05]" aria-label="Tilbake" data-testid="thread-new-back"><ArrowLeft className="h-4 w-4" /></button> : null}
        <h2 className="min-w-0 flex-1 text-[15px] font-semibold tracking-[-0.01em]">{privat ? <span className="inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-muted-foreground" /> Ny privat samtale</span> : "Ny samtale"}</h2>
        {!mobile ? <button type="button" onClick={onCancel} className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-black/[0.05] hover:text-foreground" aria-label="Avbryt" data-testid="thread-new-cancel"><X className="h-4 w-4" /></button> : null}
      </header>
      <div className="flex min-h-0 flex-1 flex-col items-center justify-end overflow-y-auto px-4 pb-4 scrollbar-thin sm:px-6">
        <div className="w-full max-w-[760px]">
          {privat ? <div className="mb-3 rounded-[14px] bg-black/[0.03] px-3.5 py-1.5"><RecipientPicker value={to} onChange={setTo} autoFocus label="Med:" testId="thread-new-to" /></div> : null}
          {chosen.length ? (
            <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="thread-new-items">
              {chosen.map((i) => (
                <span key={i.id} className="inline-flex h-7 items-center gap-1.5 rounded-full bg-[hsl(var(--navy)/0.07)] pl-2.5 pr-1 text-[12.5px] font-medium text-[hsl(var(--navy))]" data-testid={`thread-new-item-${i.ref}`}>
                  <span className="font-mono text-[11px]">{i.ref}</span><span className="max-w-[260px] truncate">{i.title}</span>
                  <button type="button" onClick={() => setItemIds((v) => v.filter((x) => x !== i.id))} className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-[hsl(var(--navy)/0.1)]" aria-label={`Fjern ${i.ref}`}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          ) : null}
          <Composer tid="new" ctx={ctx} kind={privat ? "privat" : "intern"} autoFocus={!privat} placeholder={chosen.length ? `Skriv om ${chosen[0].ref} …` : privat ? "Skriv til de du har valgt …" : "Skriv til teamet …"} busy={busy} upload={(f) => ops.thread.upload(f)} onSend={send} testId="thread-new-composer" minRows={2} extra={link} />
        </div>
      </div>
    </div>
  );
}
